/**
 * The one vertex program every world batch shares, and one fragment program per
 * visual mode.
 *
 * Instances carry position, a 2-component scale, a rotation and four free params
 * rather than a `mat4`. A 200-enemy frame therefore uploads 200 × 12 floats instead of
 * 200 × 16 plus the CPU cost of composing the matrices, and nothing per-frame has to
 * touch `THREE.Matrix4` at all.
 */

import { GLSL_WARP } from '../warp';
import { GLSL_COLOR } from './common';

/**
 * AMD-24 note: `warpWorld` is applied to the instance ORIGIN, not to the transformed
 * vertex. The seam wrap has to move a whole instance or the quad tears across the cut,
 * and the curvature has to leave the silhouette alone or a telegraph at the frame rim
 * stops being the shape the §11.9 contrast argument measured.
 */
export const INSTANCED_VERT = /* glsl */ `
precision highp float;

uniform mat4 projectionMatrix;
uniform mat4 modelViewMatrix;

in vec3 position;
in vec2 iPos;
in vec2 iScale;
in float iRot;
in vec3 iColor;
in vec4 iParams;

out vec2 vLocal;
out vec3 vColor;
out vec4 vParams;
/** World units per unit of vLocal. Lets a fragment measure a real distance. */
out vec2 vScale;

${GLSL_WARP}

void main() {
  vLocal = position.xy;
  vColor = iColor;
  vParams = iParams;
  vScale = iScale;

  vec2 p = position.xy * iScale;
  float c = cos(iRot);
  float s = sin(iRot);
  vec2 w = vec2(p.x * c - p.y * s, p.x * s + p.y * c) + warpWorld(iPos);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(w, 0.0, 1.0);
}
`;

const HEAD = /* glsl */ `
precision highp float;

in vec2 vLocal;
in vec3 vColor;
in vec4 vParams;
in vec2 vScale;
out vec4 fragColor;

${GLSL_COLOR}
`;

/**
 * Bodies: enemies, bosses, the player. `vParams` is (alpha, emissive, spawn, death).
 *
 * Births and deaths are read straight off `enemy.spawn_anim` / `enemy.death_anim`
 * (0..1, driven by R3) so nothing ever pops. The two ends of a life run the SAME stable
 * per-fragment hash in opposite directions: mass condenses out of the water on the way
 * in, mass erodes back into it on the way out. One vocabulary, learned once.
 *
 * The birth used to be an outward shimmer band riding a slow scale-in. Two problems.
 * The band swept a bright ring across the silhouette at exactly the moment §8.11 wants
 * that silhouette legible, and an alpha ramp on an already-solid shape reads as a fade
 * rather than as an arrival — nothing about it said *matter*. The dissolve does: the
 * body is speckle at first, and coalesces into a recognisable mass over the window.
 *
 * THE MATERIALISATION CONTRACT. `spawn` is `enemy.spawn_anim`, and the sim flips
 * `targetable` on the exact tick it reaches 1 (`src/sim/enemies.ts:1105`). Every damage
 * path gates on that flag (`isTargetable`, `src/sim/targeting.ts:206`), so this branch
 * is by construction "cannot be hit yet", and nothing drawn inside it may look like
 * matter. `appear` used to complete at 0.72 and the body then held a finished, opaque,
 * motionless silhouette for the last ~0.45 s of a 1.5 s telegraph — a picture that said
 * "this is a real thing" while shots still flew through it. Players read that as broken
 * collision, and they were right to.
 *
 * So coverage is capped at `GHOST_COVER` for the whole window and the alpha ceiling at
 * `GHOST_A0 + GHOST_A1`: the body is never more than a little over half its cells and
 * never more than half opaque. The remaining mass arrives in ONE frame, when this branch
 * stops being taken — which is the same frame the sim emits `materialize` and the same
 * frame the pure-white outline appears. The transition is a snap on purpose; §8.11's
 * warning is carried by the pre-image silhouette, the iris and the archetype hue, all of
 * which the renderer draws around this, not by pretending the thing has finished
 * arriving.
 */
export const FRAG_BODY = /* glsl */ `
${HEAD}

void main() {
  float alpha = vParams.x;
  float emissive = vParams.y;
  float spawn = vParams.z;
  float death = vParams.w;

  vec3 c = vColor * emissive;

  if (spawn < 1.0) {
    // The same 26-cell grid the death dissolve uses, so a body breaks up into the same
    // grain it was assembled from. The grid is in LOCAL space, so the pattern is welded
    // to the silhouette and does not crawl while the CPU-side scale swells.
    //
    // The threshold keeps climbing right up to spawn = 1 so cells keep arriving for the
    // whole telegraph — there is no motionless hold — but it climbs toward GHOST_COVER,
    // not toward 1. Just under half the mass is missing on the last pre-material frame.
    float t = smoothstep(0.02, 1.0, spawn);
    float appear = 0.55 * t;
    float n = hash12(floor(vLocal * 26.0));
    if (n > appear) discard;
    // The cells that have only just crossed the threshold keep a cold rim for a moment.
    // This is the whole "materialising" read: without it the body simply gains cells and
    // looks like a dither pattern resolving, with it the frontier itself glows and the
    // shape looks like it is being precipitated out of the water.
    //
    // The frontier brightens over the last 30% instead of fading out. That is the
    // anticipation beat the old "finished and motionless" hold used to provide, moved
    // onto something a player cannot mistake for a body that has arrived.
    float fresh = smoothstep(appear - 0.22, appear, n);
    float charge = smoothstep(0.70, 1.0, spawn);
    c += vec3(0.42, 0.68, 0.86) * fresh * (0.55 + 0.85 * charge);
    // Ceiling 0.52, floor 0.28. Combined with the coverage cap the pre-material body
    // peaks near 0.29 effective opacity — under a third of the live bodies beside it.
    alpha *= 0.28 + 0.24 * t;
  }

  if (death > 0.0) {
    float keep = 1.0 - death;
    float n = hash12(floor(vLocal * 26.0));
    if (n > keep * 1.25) discard;
    c += vColor * death * 2.2;
    alpha *= 1.0 - death * 0.35;
  }

  fragColor = vec4(c, alpha);
}
`;

/** Flat emissive fill: projectiles, pickups, aim-line cores. */
export const FRAG_SOLID = /* glsl */ `
${HEAD}

void main() {
  fragColor = vec4(vColor * vParams.y, vParams.x);
}
`;

/**
 * Rings, rims and the elite outline pulse. Geometry is already an annulus; this only
 * softens the two edges and applies the pulse in `vParams.z` (§12: 2 Hz, white).
 */
export const FRAG_RING = /* glsl */ `
${HEAD}

void main() {
  float r = length(vLocal);
  float pulse = 0.75 + 0.25 * vParams.z;

  // AMD-7 R3/R10 — vParams.w > 0 switches the annulus to a WORLD-width rim: a 2 px
  // core plus a skirt vParams.w wide at 0.35. The geometry's band is a fraction of
  // the instance radius, so a 7.7 u boss ring drew a 61 px slab of saturated cyan that
  // out-peaked the player. A rim measured in world units stays a rim at any body size.
  if (vParams.w > 0.0) {
    float d = (1.0 - r) * max(vScale.x, 1e-4);
    if (d < 0.0) discard;
    float coreW = 2.0 / 36.0;
    float band = 1.0 - smoothstep(coreW * 0.5, coreW * 1.6, d);
    float skirt = 1.0 - smoothstep(0.0, vParams.w, d);
    float a = max(band, skirt * skirt * 0.35);
    fragColor = vec4(vColor * vParams.y * pulse, vParams.x * a);
    return;
  }

  float edge = smoothstep(1.0, 0.94, r) * smoothstep(0.78, 0.86, r);
  fragColor = vec4(vColor * vParams.y * pulse, vParams.x * edge);
}
`;

/**
 * Hazard striped edge (§11.9): 45° diagonal stripes alternating fill and stripe hue,
 * 0.5 u period, scrolling 2 u/s toward the interior. Normal blending only — additive
 * is forbidden on anything telegraph-class, and this never dims under density (§11.10).
 *
 * `vParams` is (alpha, emissive, stripePeriod, scroll). `vColor` is the fill hue; the
 * stripe hue arrives as a uniform because it is `pal.hazard.stripe` for every hazard.
 */
export const FRAG_STRIPE = /* glsl */ `
${HEAD}

uniform vec3 uStripeColor;
/** x = §11.9's 0.40 u band width in world units, y = 0 for an annulus, 1 for a frame. */
uniform vec2 uBand;

void main() {
  // The band comes from the geometry (an annulus for circular zones, a frame for
  // rectangular ones), so no radial mask is applied here and a rectangular edge keeps
  // its corners.
  //
  // AMD-6 D3: the geometry's band is a *fraction* of the zone, so a 40 u hazard used to
  // arrive as an 8 u slab of flat magenta that read as a UI panel. Masking to a constant
  // world width turns it back into a border on a zone.
  vec2 sc = max(vScale, vec2(1e-4));
  float inward;
  if (uBand.y > 0.5) {
    vec2 e = (vec2(1.0) - abs(vLocal)) * sc;
    inward = min(e.x, e.y);
  } else {
    inward = (1.0 - length(vLocal)) * sc.x;
  }
  float band = 1.0 - smoothstep(uBand.x * 0.7, uBand.x, inward);

  float d = (vLocal.x + vLocal.y) * 0.70710678;
  float s = step(0.5, fract(d * vParams.z - vParams.w));
  vec3 c = mix(vColor, uStripeColor, s) * vParams.y;
  fragColor = vec4(c, vParams.x * band);
}
`;

/**
 * Particles. A soft quadratic falloff on a quad; cheaper and steadier under bloom than
 * a textured sprite, and it keeps the additive budget (§11.4) predictable per layer.
 */
export const FRAG_SPRITE = /* glsl */ `
${HEAD}

void main() {
  float r = length(vLocal);
  float f = max(0.0, 1.0 - r);
  f *= f;
  float core = smoothstep(0.55, 0.0, r);
  fragColor = vec4(vColor * vParams.y * (f + core * 0.8), vParams.x * f);
}
`;

/**
 * Damage numbers and popups. One instance per glyph, one draw for the whole layer
 * (§14.2). `vParams.z` is the glyph index into a single-row atlas; `vParams.w` carries
 * the crit pop so the 80 ms scale kick needs no second attribute.
 */
export const FRAG_GLYPH = /* glsl */ `
${HEAD}

uniform sampler2D tGlyph;
uniform float uGlyphCount;
/** x,y = one atlas texel in cell-local units; z = the quad's overscan factor. */
uniform vec3 uOutline;
uniform vec3 uOutlineColor;

float cell(vec2 p) {
  if (p.x < 0.0 || p.x > 1.0 || p.y < 0.0 || p.y > 1.0) return 0.0;
  float u = (vParams.z + clamp(p.x, 0.001, 0.999)) / uGlyphCount;
  return texture(tGlyph, vec2(u, 1.0 - clamp(p.y, 0.001, 0.999))).r;
}

void main() {
  /*
   * Pass 2 is the numeral's ground: one soft dark slab per popup, under the whole run.
   *
   * A numeral over open water measures 7-12:1 and the same numeral over the Abyssal Warden's
   * lit body measures 2.30:1 -- under even the 3:1 that 13.1 asks of large text, and
   * unreadable at 9x. Every lever that already existed acts on the INK and none of them
   * reaches: a wider keyline buys 0.02, and doubling the face emissive buys 0.5 while
   * re-introducing the noisier cluster a prior blind A/B rejected. What changes under a
   * drifting popup is the GROUND, so the ground is what has to be supplied.
   *
   * No atlas sample: this is the quad, shaped by a soft edge so it reads as shadow the
   * numbers cast rather than as a label pasted into the playfield.
   */
  if (vParams.w > 1.5) {
    vec2 q = abs(vLocal);
    float e = 1.0 - max(q.x, q.y);
    fragColor = vec4(uOutlineColor, vParams.x * vParams.y * smoothstep(0.0, 0.45, e));
    return;
  }

  // The instance quad is uOutline.z larger than the glyph cell so the dilation taps
  // land on empty margin instead of the neighbouring glyph in the atlas row.
  vec2 p = vLocal * uOutline.z * 0.5 + 0.5;
  float m = cell(p);

  // §11.11 keeps the ramp; AMD-6 D8 adds the 2 px black outline so a white number stays
  // legible over a bloomed friendly projectile. vParams.w selects the pass: the caller
  // emits every outline first and every fill second, because the expanded quads overlap
  // and a per-glyph outline would otherwise paint over its own neighbour's face.
  if (vParams.w > 0.5) {
    vec2 d = uOutline.xy;
    float o = max(m, max(
      max(max(cell(p + vec2(d.x, 0.0)), cell(p - vec2(d.x, 0.0))),
          max(cell(p + vec2(0.0, d.y)), cell(p - vec2(0.0, d.y)))),
      max(max(cell(p + d), cell(p - d)),
          max(cell(p + vec2(d.x, -d.y)), cell(p + vec2(-d.x, d.y))))));
    if (o < 0.5) discard;
    fragColor = vec4(uOutlineColor, vParams.x);
    return;
  }

  if (m < 0.5) discard;
  fragColor = vec4(vColor * vParams.y, vParams.x);
}
`;

/**
 * Beams. Geometry runs x 0..1 along the beam and y -1..1 across it, so instance scale
 * is (length, half-width) directly. A white-hot core inside the hue satisfies §11.9's
 * "attacker hue + white core" for aim lines and reads as a beam for the two weapons.
 */
export const FRAG_BEAM = /* glsl */ `
${HEAD}

void main() {
  // AMD-7 R2a — vParams.w is the taper amount, 0..1.
  //
  // A constant-width, constant-alpha line is the "rope" defect: seven searchlight beams
  // leaving one muzzle arrived as seven identical bars and nothing in the frame said
  // which way anything was moving. At taper 1 the strip narrows to a quarter width and
  // its alpha reaches zero across the last 25% of its length, so the far end resolves as
  // a direction rather than a cut. §11.9 aim lines pass 0 and keep their pinned width
  // and full alpha — a telegraph must never thin.
  float taper = clamp(vParams.w, 0.0, 1.0);
  float along = clamp(vLocal.x, 0.0, 1.0);
  float narrow = mix(1.0, mix(1.0, 0.25, along), taper);
  float across = clamp(1.0 - abs(vLocal.y) / max(narrow, 1e-3), 0.0, 1.0);
  float body = across * across;
  float core = smoothstep(0.72, 1.0, across);
  float tip = smoothstep(1.0, 0.86, vLocal.x);
  float fade = mix(1.0, smoothstep(1.0, 0.75, along), taper);
  // AMD-7 R3: §11.9's white-core convention belongs to the telegraph aim line, which
  // passes taper 0 and keeps it at full strength. On a weapon beam the same term put a
  // near-white core inside an already-2.0-emissive hue and measured 0.99 luminance —
  // a tie with the player. Weapon beams take a third less white and keep their hue.
  float coreMix = mix(0.55, 0.34, taper);
  vec3 c = vColor * vParams.y * body + vec3(1.0) * core * vParams.y * coreMix;
  fragColor = vec4(c * tip * fade, vParams.x * max(body, core) * tip * fade);
}
`;

/**
 * XP motes. Spine §12 double-codes denominations by ring count (0/1/2 = 1/5/25); the
 * rings are drawn inside the droplet here so the whole pickup layer stays one draw.
 * `vParams.z` is the ring count, `vParams.w` the bob phase.
 *
 * AMD-41 — the rings are rebuilt, because three separate things stopped them working
 * and a playtester could not tell a 1 from a 5 from a 25.
 *
 * 1. They were measured from the instance ORIGIN, but `buildMoteGeometry` puts the
 *    droplet's bulb at (0.05, 0) with radius 0.72, so the rim sat at local r 0.67–0.77.
 *    Ring one at 0.62 grazed the rim; ring two at 0.86 fell OUTSIDE the bulb entirely
 *    and only ever intersected the tail fan. The 25-mote's second ring was never a ring.
 *    `r` is now the bulb's own normalised radius, so 0.46 and 0.78 are real circles.
 *
 * 2. The stroke was a fixed 0.06 of local space. A 15 px reference radius is 8.4 CSS px
 *    on a 390×844 phone (42 u of view height over 844 rows), so the stroke resolved to
 *    0.5 px — sub-pixel before the 0.85-strength bloom smeared it. The width is now
 *    `fwidth`-clamped, which pins it at ≥ 1.6 px on any viewport at any resolution.
 *
 * 3. They ADDED light. The mote hue's green channel is already 255 and the batch blends
 *    additively, so an additive ring on a saturated mote can only push the pixel toward
 *    white, and bloom takes it the rest of the way. The rings are now grooves cut out
 *    of the droplet: alpha goes to zero across the band, so the abyssal background shows
 *    through. Bloom can bleed light into a groove's edges but cannot fill a hole it is
 *    not lighting, which is what makes this the code that survives the post chain.
 */
export const FRAG_MOTE = /* glsl */ `
${HEAD}

/** Bulb centre and radius, from \`buildMoteGeometry\`'s normalised droplet. */
const vec2 MOTE_BULB_C = vec2(0.05, 0.0);
const float MOTE_BULB_R = 0.72;
const float MOTE_RING_INNER = 0.46;
const float MOTE_RING_OUTER = 0.78;
/** Minimum groove half-width, in fragments. Below ~1.5 px a groove is not a groove. */
const float MOTE_GROOVE_PX = 1.6;

void main() {
  float r = length(vLocal - MOTE_BULB_C) / MOTE_BULB_R;
  vec3 c = vColor * vParams.y;
  // AMD-6 D15: the inner core boost is trimmed. A pickup that peaks as hard as a lethal
  // body inverts the priority the whole readability argument depends on.
  c += vColor * smoothstep(0.47, 0.0, r) * (0.22 + 0.08 * sin(vParams.w));

  // Distance to the nearest groove this tier carries; one distance, so the two bands
  // cost two smoothsteps between them rather than two each.
  float rings = vParams.z;
  float d = rings >= 1.0 ? abs(r - MOTE_RING_INNER) : 1e3;
  if (rings >= 2.0) d = min(d, abs(r - MOTE_RING_OUTER));
  float w = max(0.075, fwidth(r) * MOTE_GROOVE_PX);
  float groove = 1.0 - smoothstep(0.0, w, d);
  // The rim either side of a groove is lifted: a dark band between two bright bands is
  // legible a good deal further out than a dark band alone.
  float shoulder = 1.0 - smoothstep(0.0, w * 2.6, d);
  c += vColor * max(shoulder - groove, 0.0) * 0.55;

  fragColor = vec4(c, vParams.x * (1.0 - 0.94 * groove));
}
`;

/**
 * AMD-6 D4 — the outer two of the three neon layers.
 *
 * `vParams.z` is the falloff exponent, which is the whole knob:
 *  - halo, drawn at 3× the entity radius with exponent 1.1, puts its 50%-intensity
 *    point at 1.4× the radius and reaches zero at 3×;
 *  - wide bleed, drawn at 8× with exponent 2.0 at 0.04 alpha, restores the ambience
 *    the shrunken player aura used to provide;
 *  - the player aura itself, exponent 5.0, which puts ~79% of its energy inside 40 px.
 *
 * Colour is NOT multiplied by the falloff — alpha is. Additive blending is
 * `src.rgb * src.a + dst`, so the contribution is linear in the falloff rather than
 * quadratic, and the 12% / 4% alpha caps mean what they say.
 */
export const FRAG_HALO = /* glsl */ `
${HEAD}

void main() {
  float u = length(vLocal);
  if (u >= 1.0) discard;
  float f = pow(1.0 - u, max(vParams.z, 0.05));
  fragColor = vec4(vColor * vParams.y, vParams.x * f);
}
`;

/**
 * AMD-6 D1 — the boss field, replacing the screen-wide violet wash.
 *
 * Three terms, in the order the art direction states them: a hard rim at full chroma,
 * an inner fill whose alpha the CPU caps at 0.12, and a radial punch-out that returns
 * the fill to zero inside `uPunch.z` of the player so the ~260 px the player actually
 * reads stays at the abyssal floor.
 *
 * `uPunch.xy` is the player in the instance's local frame, `uPunch.z` the punch radius
 * in the same units; `uRim` is the rim width. One boss is ever alive, so these are
 * batch uniforms rather than per-instance attributes.
 */
export const FRAG_BOSS_FIELD = /* glsl */ `
${HEAD}

uniform vec3 uPunch;
/** AMD-7 R10: x = 2 px core half-width, y = 10 px skirt, both in instance-local units. */
uniform vec2 uRim;

void main() {
  float u = length(vLocal);
  if (u > 1.0) discard;

  // AMD-7 R10 — the edge was one hard smoothstep, i.e. a flat neon stroke with nothing
  // outside it. It is now a 2 px core at full alpha plus a 10 px gaussian skirt at 0.35,
  // which is what stops the boundary reading as a drawn line on a UI panel.
  float d = 1.0 - u;
  float core = 1.0 - smoothstep(uRim.x * 0.5, uRim.x * 1.6, d);
  float g = d / max(uRim.y, 1e-4);
  float skirt = exp(-g * g * 2.4);
  float rim = max(core, skirt * 0.35);

  float dp = length(vLocal - uPunch.xy) / max(uPunch.z, 1e-4);
  float punch = smoothstep(0.45, 1.0, dp);
  float fill = vParams.x * punch * (0.55 + 0.45 * u) * (1.0 - rim);
  fragColor = vec4(vColor * vParams.y, max(fill, rim * vParams.z));
}
`;

/**
 * AMD-6 D3 — hazard zone fill.
 *
 * §11.9's ramp/hold semantics are unchanged: `vParams.x` is the 0.15 → 0.50 ramp and it
 * still holds after the arm tick. What changed is what that number drives. The interior
 * is 0.36× it (0.18 at hold), so the zone stops reading as a UI panel; the remaining
 * energy goes into a 6 px hard leading edge at full chroma, a 24 px gradient inward from
 * it, and chevrons on a 60 px pitch marching along the edge normal at 120 px/s.
 *
 * The 45° stripe double-code (§11.12 rule 2) lives on the separate edge-band batch and
 * is untouched. Normal blending only, per §11.4.
 *
 * `vParams` is (ramp, emissive, shape+warn, scroll). Shape is the integer part —
 * 0 disc, 1 rect — and the fractional part is the 0.5 s advance-warning pulse.
 */
export const FRAG_HAZARD_ZONE = /* glsl */ `
${HEAD}

uniform vec3 uStripeColor;
uniform vec3 uMetrics;

void main() {
  vec2 s = max(vScale, vec2(1e-4));
  float shape = floor(vParams.z);
  float warn = vParams.z - shape;

  float d;
  if (shape > 0.5) {
    vec2 e = (vec2(1.0) - abs(vLocal)) * s;
    d = min(e.x, e.y);
  } else {
    float u = length(vLocal);
    if (u > 1.0) discard;
    d = (1.0 - u) * s.x;
  }

  float ramp = clamp(vParams.x, 0.0, 1.0);

  // Three terms, each with its own colour, composited by whichever is loudest. The
  // interior deliberately runs at a fraction of the telegraph emissive: an 18% alpha of
  // a 1.4× emissive magenta is not an 18% wash, it is the flat panel this defect is about.
  float interior = ramp * 0.3;
  float grad = (1.0 - smoothstep(0.0, uMetrics.y, d)) * ramp * 0.4;
  float lead = 1.0 - smoothstep(uMetrics.x * 0.55, uMetrics.x, d);
  float march = fract((d - vParams.w) / uMetrics.z);
  float chev = smoothstep(0.55, 0.92, march) * (1.0 - smoothstep(uMetrics.z * 2.5, uMetrics.z * 6.0, d));

  vec3 c = vColor * 0.5;
  float a = min(1.0, interior + grad);

  float chevA = chev * ramp * 0.32;
  if (chevA > a) {
    c = mix(vColor, uStripeColor, 0.22) * vParams.y * 0.55;
    a = chevA;
  }
  float leadA = lead * (0.2 + 0.75 * warn);
  if (leadA > a) {
    c = mix(vColor, uStripeColor, 0.35) * vParams.y;
    a = leadA;
  }
  fragColor = vec4(c, clamp(a, 0.0, 1.0));
}
`;

/**
 * §10.7.4 — the storm veil. The pass that takes long-range awareness away.
 *
 * **It composites toward a capped haze, and the first draft did not.**
 *
 * The first version multiplied — `ZERO, SRC_COLOR` — which made AMD-6's "no pass may
 * raise background luminance above 0.06" true by construction and looked like absolutely
 * nothing. The captures are why it changed: the abyssal floor sits at 0.003 linear, and
 * *you cannot see light being removed from black*. A multiply veil dimmed the handful of
 * lit things at the frame edge and left the player with no sense that the weather had
 * changed at all.
 *
 * So the veil now mixes toward `uHaze`, a colour whose own luminance is capped under the
 * AMD-6 ceiling. That keeps the guarantee constructive, just by a different argument:
 * `mix(a, b, t) <= max(a, b)` for every t in [0, 1], so this pass can only ever move a
 * pixel BETWEEN the value it had and the haze. The floor shader already clamps the
 * background to 0.06 and the haze is under it, so the composite is too — and no
 * measurement is load-bearing for that claim either.
 *
 * The information removal survives the change intact, and is in fact stronger: a distant
 * enemy at 0.17 linear composites to 0.08 AND is pulled toward the haze hue, so it loses
 * chroma as well as luminance. What it gains is that the storm is now visible.
 *
 * **It has a hole in it, and the hole is the §5.8 combat ring.** `uEye` carries the
 * ship's offset from the instance centre in world units, plus the protected radius and
 * the feather width. Inside `protect` the multiplier is exactly 1: not "almost", not
 * "0.98" — the branch returns before any noise term is applied, so the 18 u disc the
 * weapons acquire inside is bit-identical to a clear frame. Past it the veil ramps over
 * `feather` to `uDepth.x`, which is capped well short of 1 so distant bodies are
 * attenuated rather than erased.
 *
 * **The structure is two octaves, not seven.** A domain-warped fbm reads better and
 * costs a phone its frame budget; two hash octaves scrolling on different bearings give
 * the veil a moving grain, which is all it needs to stop reading as a lens vignette.
 * `uDepth.y` is the scroll phase and `uDepth.z` the noise depth, which the CPU takes to
 * zero on the bottom quality rungs so the veil degrades to a smooth gradient instead of
 * dropping frames.
 */
export const FRAG_STORM_VEIL = /* glsl */ `
${HEAD}

/** xy = ship offset from the instance centre (world u), z = protected radius, w = feather. */
uniform vec4 uEye;
/** x = peak occlusion, y = scroll phase, z = noise depth. */
uniform vec3 uDepth;
/**
 * The storm's own colour, in linear RGB, already scaled on the CPU to sit under the
 * AMD-6 background ceiling. Drawn from \`pal.ui.decor\` — the sonar-instrument family,
 * which is the family that already means "this is the deep, not a thing in it". A storm
 * in a reserved gameplay hue would be a screen-wide telegraph that means nothing.
 */
uniform vec3 uHaze;

float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash12(i);
  float b = hash12(i + vec2(1.0, 0.0));
  float c = hash12(i + vec2(0.0, 1.0));
  float d = hash12(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

void main() {
  // World offset from the ship. vLocal is the unit quad, vScale its half-extent in
  // world units, so this is a real distance in the same units §5.8 is stated in.
  vec2 w = vLocal * vScale;
  float dist = length(w - uEye.xy);

  // THE PROTECTED DISC. Everything the player can shoot is inside it, and nothing this
  // program does reaches in. Alpha 0 is the identity of a normal blend, and the branch
  // returns before the noise term, so the 18 u combat ring is bit-identical to a clear
  // frame rather than merely close to one.
  if (dist <= uEye.z) {
    fragColor = vec4(uHaze, 0.0);
    return;
  }

  float ramp = smoothstep(uEye.z, uEye.z + max(uEye.w, 1e-3), dist);
  float occ = uDepth.x * ramp;

  if (uDepth.z > 0.0) {
    vec2 q = w * 0.09;
    float n = vnoise(q + vec2(uDepth.y, uDepth.y * 0.6)) * 0.62
            + vnoise(q * 2.3 - vec2(uDepth.y * 0.8, uDepth.y * 1.7)) * 0.38;
    // Centred on zero so the grain thickens and thins the veil rather than only
    // darkening it — a cloud deck has holes, and the holes are the readable part.
    occ *= 1.0 + uDepth.z * (n - 0.5) * 2.0;
  }

  occ = clamp(occ, 0.0, 1.0);
  fragColor = vec4(uHaze, occ);
}
`;

/**
 * §11.16 — a gas cloud.
 *
 * **What it does NOT have is a hue of its own.** The AMD-46 measurement found no room
 * for one: across the whole wheel, at every saturation, the best worst-case OKLab ΔE a
 * new colour can reach against the 62 meaning-carrying tokens is 5.0, against a floor of
 * 20, and at the luminance AMD-6 allows a background element the ceiling is 13.9 —
 * limited by `pal.enemy.basalt-crab`, which sits at almost exactly the same luminance a
 * legal cloud does. So the cloud borrows instead of spending.
 *
 * It borrows from the two real dynamic lights of §14.1 B8, the same pair that light the
 * trench floor. A cloud near the ship scatters the player's warm gold; a cloud near a
 * boss scatters that boss's primary; a cloud in open water is the dark blue-grey of the
 * sonar chrome it is drawn in. Every colour it can show is already in the frame and
 * already means what it means, so "various bright colours" costs nothing from §11's
 * budget and cannot teach the player a code that collides with one they have.
 *
 * The AMD-6 D1 headroom clamp is applied here exactly as `FLOOR_FRAG` applies it, so a
 * lit cloud obeys the 0.06 background ceiling for the same reason the lit floor does.
 *
 * `vParams` is (alpha, emissive, scrollPhase, octaves).
 */
export const FRAG_GAS = /* glsl */ `
${HEAD}

uniform vec2 uLight0Pos;
uniform vec3 uLight0Color;
uniform float uLight0Range;
uniform vec2 uLight1Pos;
uniform vec3 uLight1Color;
uniform float uLight1Range;
/** Instance centre in world units — the fragment needs it to place itself for the lights. */
uniform vec2 uOrigin;

float vnoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash12(i);
  float b = hash12(i + vec2(1.0, 0.0));
  float c = hash12(i + vec2(0.0, 1.0));
  float d = hash12(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}

void main() {
  float u = length(vLocal);
  if (u >= 1.0) discard;

  // Soft body. The falloff is quartic so the cloud has no edge the eye can catch: an
  // edge is what would let a 20 u wash be mistaken for a 1 u body, and every enemy in
  // this game wears a 2 px pure-white outline precisely because edges mean "object".
  float body = 1.0 - u;
  body *= body;

  vec2 w = vLocal * vScale;
  float phase = vParams.z;
  float n;
  if (vParams.w > 1.5) {
    // Domain warp, canvasui's Clouds treatment at a phone's budget: one warp lookup
    // feeding two octaves, rather than a seven-octave fbm per fragment.
    vec2 q = w * 0.13 + vec2(phase * 0.5, phase * 0.31);
    vec2 warp = vec2(vnoise(q), vnoise(q + 5.2)) - 0.5;
    vec2 p = w * 0.21 + warp * 1.7 + vec2(phase, -phase * 0.42);
    n = vnoise(p) * 0.62 + vnoise(p * 2.1 + 3.7) * 0.38;
  } else {
    n = vnoise(w * 0.19 + vec2(phase, -phase * 0.42));
  }
  // Remapped so the cloud is mostly thin with a few dense cores, which is what makes a
  // drifting field read as gas instead of as a bank of identical blobs.
  float density = body * smoothstep(0.28, 0.86, n * 0.75 + body * 0.45);

  vec3 c = vColor * vParams.y;

  // The borrowed colour. AMD-24: no toroidal delta is needed — a cloud is culled long
  // before it is a half-period from a light, so the direct difference is the short arc.
  vec2 wp = uOrigin + w;
  float d0 = length(wp - uLight0Pos);
  float a0 = max(0.0, 1.0 - d0 / max(uLight0Range, 0.001));
  float d1 = length(wp - uLight1Pos);
  float a1 = max(0.0, 1.0 - d1 / max(uLight1Range, 0.001));
  vec3 lit = uLight0Color * a0 * a0 * 0.85 + uLight1Color * a1 * a1 * 0.7;

  // AMD-6 D1's hard rule, applied the way FLOOR_FRAG applies it: the scattered light
  // may fill the headroom under 0.06 and not one part past it.
  float headroom = max(0.0, 0.06 - luma(c));
  float litLuma = luma(lit);
  if (litLuma > headroom) lit *= headroom / max(litLuma, 1e-5);
  c += lit;

  fragColor = vec4(c, vParams.x * density);
}
`;

/**
 * AMD-6 D7 — the destruction shockwave ring.
 *
 * The ring rides the outer edge of a unit disc and keeps a constant *world* thickness
 * (`vParams.z`), so a 2 px ring stays 2 px while its radius sweeps 0 → 90 px. A ring
 * built from an annulus geometry would fatten with its own radius instead.
 */
export const FRAG_SHOCK = /* glsl */ `
${HEAD}

void main() {
  float u = length(vLocal);
  if (u > 1.0) discard;
  float r = vScale.x;
  float band = 1.0 - smoothstep(0.0, max(vParams.z, 1e-4), abs(r - u * r));
  if (band <= 0.001) discard;
  fragColor = vec4(vColor * vParams.y, vParams.x * band);
}
`;

/**
 * AMD-6 D14's world-space off-screen chevron. Retired by AMD-8, which moved the
 * off-screen threat signal into the screen-space overlay in `radar.ts`; the mode slot
 * is kept so the ids either side of it do not renumber. No batch uses it.
 */
export const FRAG_CHEVRON = /* glsl */ `
${HEAD}

void main() {
  fragColor = vec4(vColor * vParams.y, vParams.x * (0.72 + 0.28 * vParams.z));
}
`;

/**
 * AMD-8 — the threat radar and the off-screen threat arrows.
 *
 * One program for every mark the overlay draws. The overlay renders to the default
 * framebuffer *after* the CRT composite, so two things follow that no other fragment
 * program here has to deal with:
 *
 *  - the destination is already sRGB-encoded, so this shader encodes on the way out
 *    rather than leaving a linear value for a later tonemap that will never run;
 *  - the geometry is always the unit quad, and the shape is an SDF selected by
 *    `vParams.z`. Seven shapes through one geometry is what keeps the whole overlay
 *    at two draw calls instead of one per mark class (§14.2 B7).
 *
 * `vParams` is (alpha, emissive, shape, aux). `aux` is a pixel width for the shapes
 * that need one — the plate feather and the frame line — and is unused elsewhere.
 * `vScale` carries the instance's half-extent in output pixels, which is what lets a
 * frame keep a constant 1 px line on a 300 × 40 rectangle.
 */
export const FRAG_RADAR = /* glsl */ `
${HEAD}

void main() {
  vec2 p = vLocal;
  float shape = vParams.z;
  float aux = vParams.w;
  float a;

  if (shape < 0.5) {
    // 0 — plate. Soft-edged rectangle; aux is the feather in pixels.
    vec2 e = (vec2(1.0) - abs(p)) * vScale;
    a = smoothstep(0.0, max(aux, 0.5), min(e.x, e.y));
  } else if (shape < 1.5) {
    // 1 — frame. aux is the line width in pixels, measured inward from the border.
    vec2 e = (vec2(1.0) - abs(p)) * vScale;
    float d = min(e.x, e.y);
    a = (1.0 - smoothstep(aux, aux + 1.0, d)) * smoothstep(-0.5, 0.5, d);
  } else if (shape < 2.5) {
    // 2 — dot. A hard core with one pixel of skirt, so a 3 px blip is still a blip.
    a = smoothstep(1.0, 0.42, length(p));
  } else if (shape < 3.5) {
    // 3 — diamond (elite). §11.12 rule 2: shape carries the code, not colour alone.
    a = 1.0 - smoothstep(0.72, 1.0, abs(p.x) + abs(p.y));
  } else if (shape < 4.5) {
    // 4 — arrowhead pointing +x, rotated by the instance bearing. Boss, and every
    // off-screen threat arrow.
    float t = (p.x + 1.0) * 0.5;
    float d = abs(p.y) - (1.0 - t);
    a = (1.0 - smoothstep(-0.08, 0.08, d)) * step(-1.0, p.x);
  } else if (shape < 5.5) {
    // 5 — cross (health).
    float arm = 1.0 - smoothstep(0.24, 0.4, min(abs(p.x), abs(p.y)));
    a = arm * (1.0 - smoothstep(0.86, 1.0, max(abs(p.x), abs(p.y))));
  } else if (shape < 6.5) {
    // 6 — square (hazard zone).
    a = 1.0 - smoothstep(0.74, 0.96, max(abs(p.x), abs(p.y)));
  } else {
    // 7 — ring.
    float r = length(p);
    a = smoothstep(0.52, 0.72, r) * (1.0 - smoothstep(0.88, 1.0, r));
  }

  a *= vParams.x;
  if (a <= 0.002) discard;
  fragColor = vec4(linearToSrgb(clamp(vColor * vParams.y, 0.0, 1.0)), a);
}
`;

/**
 * Arena floor and sonar grid. This is the only surface lit by the 0–2 real dynamic
 * lights of spine §9 / §14.1 B8: the player aura and the boss. Everything else in the
 * scene is emissive plus bloom.
 *
 * AMD-24 turned this from a fixed 200 × 200 slab with a wall falloff at its rim into a
 * camera-following lattice with no rim at all. Two consequences:
 *
 *  - the mesh is subdivided, because the curvature is evaluated per VERTEX. A two-triangle
 *    quad would bend nothing; 32 × 32 cells put the grid on a surface;
 *  - `vWorld` is the TRUE world position and is deliberately NOT wrapped. The sonar grid
 *    has pitches of 4 u and 20 u, both of which divide the 200 u period exactly, so
 *    letting the coordinate run past ±100 draws the identical lattice and the seam is
 *    invisible for free. Everything that is not periodic in 4 u — the sweep, the ripples,
 *    the two lights — takes an explicit toroidal delta below.
 */
export const FLOOR_VERT = /* glsl */ `
precision highp float;

uniform mat4 projectionMatrix;
uniform mat4 modelViewMatrix;
/** Lattice centre (the camera focus) and half-extent, both in world units. */
uniform vec2 uFloorCenter;
uniform vec2 uFloorHalf;

in vec3 position;
out vec2 vWorld;

${GLSL_WARP}

void main() {
  vWorld = uFloorCenter + position.xy * uFloorHalf;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(warpWorld(vWorld), 0.0, 1.0);
}
`;

export const FLOOR_FRAG = /* glsl */ `
precision highp float;

uniform vec3 uBg;
uniform vec3 uGrid;
uniform vec3 uGridMajor;
uniform vec3 uDecor;
uniform vec2 uLight0Pos;
uniform vec3 uLight0Color;
uniform float uLight0Range;
uniform vec2 uLight1Pos;
uniform vec3 uLight1Color;
uniform float uLight1Range;
uniform float uTime;
/** AMD-24: the torus period, for every term on this surface that is not 4 u-periodic. */
uniform vec2 uPeriod;
/** AMD-6 D13: four explosion ripples, (x, y, startTime, unused). */
uniform vec4 uRipples[4];

in vec2 vWorld;
out vec4 fragColor;

${GLSL_COLOR}

/** Shortest displacement on the torus. The GLSL twin of wrapDeltaX/Y in sim/wrap.ts. */
vec2 wrapDelta2(vec2 d) {
  return d - uPeriod * floor(d / uPeriod + 0.5);
}

float gridLine(vec2 p, float pitch, float width) {
  vec2 g = abs(fract(p / pitch - 0.5) - 0.5) * pitch;
  float d = min(g.x, g.y);
  return 1.0 - smoothstep(0.0, width, d);
}

void main() {
  vec3 c = uBg;

  // AMD-6 D13: explosions displace the grid by 8 px inside a 200 px radius, decaying
  // over 350 ms. The sample point moves, not the line, so the whole lattice bulges.
  vec2 gp = vWorld;
  for (int i = 0; i < 4; i++) {
    vec4 r = uRipples[i];
    float age = uTime - r.z;
    if (age < 0.0 || age > 0.35) continue;
    vec2 v = wrapDelta2(vWorld - r.xy);
    float dist = length(v);
    if (dist < 1e-4 || dist > 5.55) continue;
    float fall = (1.0 - dist / 5.55) * (1.0 - age / 0.35);
    gp += (v / dist) * fall * fall * 0.222;
  }

  // §11.1 sonar grid, alpha ≤ 0.25 combined. AMD-6 D13 lifts the hues and splits the
  // majors onto their own token so the player keeps a spatial reference on black.
  float fine = gridLine(gp, 4.0, 0.05);
  float major = gridLine(gp, 20.0, 0.11);
  c = mix(c, uGrid, fine * 0.35 * (1.0 - major));
  c = mix(c, uGridMajor, major * 0.5);

  // Slow sonar sweep, decorative only: luminance stays below every enemy hue (§11.2.6).
  //
  // AMD-24: measured from the arena origin through the torus, so the ring pattern is
  // symmetric about the seam and carries across it without a step. An unwrapped length
  // would grow without bound as the player laps the arena, and step at every crossing.
  float d = length(wrapDelta2(vWorld));
  float sweep = smoothstep(0.9, 1.0, sin(d * 0.09 - uTime * 0.55)) * 0.05;
  c += uDecor * sweep;

  // AMD-24 deleted the wall falloff with the walls themselves. There is no rim to fade.

  float d0 = length(wrapDelta2(vWorld - uLight0Pos));
  float a0 = max(0.0, 1.0 - d0 / max(uLight0Range, 0.001));
  float d1 = length(wrapDelta2(vWorld - uLight1Pos));
  float a1 = max(0.0, 1.0 - d1 / max(uLight1Range, 0.001));
  vec3 lit = uLight0Color * a0 * a0 * 0.55 + uLight1Color * a1 * a1 * 0.45;

  // AMD-6 D1 hard rule: neither of the two real lights may raise the scene background
  // above 0.06 linear luminance. Geometry Wars never lifts its background; the boss
  // presence is carried by the rim and the capped field, not by flooding the floor.
  float headroom = max(0.0, 0.06 - luma(c));
  float litLuma = luma(lit);
  if (litLuma > headroom) lit *= headroom / max(litLuma, 1e-5);
  c += lit;

  fragColor = vec4(c, 1.0);
}
`;

/**
 * Ribbon trails. Colour and alpha are per-vertex; there is no instancing here.
 *
 * AMD-24: the seam wrap is NOT applied here. A ribbon is a strip of vertices and wrapping
 * them one at a time would send half a ribbon 200 u across the arena the moment its head
 * crossed the cut. `TrailBuffer.build` resolves each ribbon to a single image on the CPU
 * instead, so this program only has to bend it onto the sphere.
 */
export const TRAIL_VERT = /* glsl */ `
precision highp float;

uniform mat4 projectionMatrix;
uniform mat4 modelViewMatrix;

in vec3 position;
in vec4 color;

out vec4 vColor;

${GLSL_WARP}

void main() {
  vColor = color;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(warpWorld(position.xy), 0.0, 1.0);
}
`;

export const TRAIL_FRAG = /* glsl */ `
precision highp float;

in vec4 vColor;
out vec4 fragColor;

void main() {
  fragColor = vec4(vColor.rgb, vColor.a);
}
`;
